import { Provider as RollbarProvider } from '@rollbar/react';
import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { QButton, QContainer, QThemeProvider, QTypography } from '@kw/quokka-ui';
import { useAuth0 } from '@auth0/auth0-react';
import { HashRouter as Router, Routes, Route, Outlet, Navigate, useLocation } from 'react-router-dom';
import { SnackbarProvider } from 'notistack';
import { ReactNode, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { createEmotionCache } from '../createEmotionCache';
import { useLocalStorage } from '../utils/useLocalStorage';
import { socket } from '../utils/rt';
import { AuthPermissions } from '../auth/AuthPermissions';
import { verifyAuthPermission } from '../auth/VerifyAuthPermission';
import { getDefaultRoute } from '../auth/getDefaultRoute';
import Index from './index';
import Applications from './Applications';
import RequestAnalysis from './Applications/RequestAnalysis';
import Devices from './Devices';
import DeviceDetails from './Devices/DeviceDetails';
import DeviceOwners from './DeviceOwners';
import Layout from './Layout/Layout';
import Policies from './Policy';
import { Settings } from './Settings';
import AppManagement from './AppManagement';
import RiskIndicators from './RiskIndicators';
import { Dashboard } from './Dashboard';
import { jwtPayload, logoutLocal } from '../auth';
import { verifyAuthExpiration } from '../auth/VerifyAuthExpiration';
import OktaTroubleshooting from './OktaTroubleshooting';
import { OrgProvider } from './Organization/org-provider';
import OrgMismatchError from './shared/OrgMismatchError';
import NoAccess from './shared/NoAccess';
import SignInDenied, { LOGIN_NOT_PERMITTED } from './shared/SignInDenied';
import { BackgroundProgressBarProvider } from './BackgroundProgressBar/BackgroundProgressBarProvider';
import { NavigationProvider } from './Navigation/NavigationProvider';
import SarifReport from './Applications/GuardianReport/SarifReport';
import { CustomerSupport } from './CustomerSupport/CustomerSupport';
import { NewReportPdfView } from './Applications/GuardianReport/PdfReport/NewPdfReport';
import { announceNewLogin, myTabId, onAuthBusMessage } from '../auth/sessionBus';
import { ACTIVE_SUB_KEY, LAST_BROADCAST_KEY, STALE_KEY, LOGOUT_KEY } from './shared/constants';
import { StaleOverlay } from './shared/StaleOverlay';
import { LoggedOutOverlay } from './shared/LoggedOutOverlay';

// extend ProviderProps for @rollbar/react Provider (RollbarProvider) to avoid overload error (bug)
declare module '@rollbar/react' {
  interface ProviderProps {
    children: ReactNode;
  }
}

function isTokenNearExpiry(token: string, skewSeconds = 60) {
  try {
    const { exp } = jwtPayload(token) as any;
    if (!exp) return true;
    const now = Math.floor(Date.now() / 1000);
    return exp - now <= skewSeconds;
  } catch {
    return true;
  }
}

function getAuthErrorFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const error = params.get('error');
  const description = params.get('error_description');
  if (!error) return null;
  if (description) return `${error}: ${description}`;
  return error;
}

export default function App() {
  ReactGA.initialize([{ trackingId: process.env.GA4_ID }]);

  const rollbarConfig = useMemo(
    () => ({
      enabled: process.env.NODE_ENV === 'production',
      accessToken: '24a2bce8fad747d094182c5682b141dd',
      captureUncaught: true,
      captureUnhandledRejections: true,
      payload: { environment: process.env.NODE_ENV },
      autoInstrument: {
        network: true,
        networkResponseHeaders: true,
        networkResponseBody: true,
        networkRequestBody: true,
      },
    }),
    [],
  );

  const cache = useMemo(() => createEmotionCache(), []);

  const [accessToken = '', updateAccessToken] = useLocalStorage('accessToken', '');
  const auth0 = useAuth0();

  const ProtectedRoute = ({ authPermission }: { authPermission: string | string[] }) => {
    const location = useLocation();

    // Wait while auth is unresolved OR resolved-as-logged-out (RequiresLogin
    // will redirect to Auth0), and while authenticated but the token hasn't
    // been written to localStorage yet. Only fall through to the default route
    // for authenticated users who lack the specific permission — otherwise we'd
    // clobber the deep link before loginWithRedirect can read it.
    if (auth0.isLoading || !auth0.isAuthenticated || !accessToken) {
      return null;
    }

    const permissions = Array.isArray(authPermission) ? authPermission : [authPermission];
    if (permissions.some(p => verifyAuthPermission(p))) {
      return <Outlet />;
    }

    // Redirect unauthorized users to a page they can actually access rather than
    // a hard-coded /dashboard. getDefaultRoute only returns routes the user is
    // permitted to view, so this won't bounce back here. When there is no such
    // route (or it somehow resolves to this same denied route), send them to the
    // no-access page instead of looping or rendering a blank screen.
    const defaultRoute = getDefaultRoute();
    if (defaultRoute && defaultRoute !== location.pathname) {
      return <Navigate to={defaultRoute} replace />;
    }
    return <Navigate to={'/no-access'} replace />;
  };
  const ldClient = useLDClient();

  const [isStale, setIsStale] = useState(() => sessionStorage.getItem(STALE_KEY) === '1');
  const [isLoggedOut, setIsLoggedOut] = useState(() => sessionStorage.getItem(LOGOUT_KEY) === '1');
  const [authRedirectError] = useState<string | null>(() => getAuthErrorFromUrl());
  const authErrorMessage = auth0.error?.message || authRedirectError;
  const [authRedirectErrorDescription] = useState(() => new URLSearchParams(window.location.search).get('error_description'));
  const signInDenied = [auth0.error?.message, authRedirectErrorDescription].includes(LOGIN_NOT_PERMITTED);
  const handleReturnToLogin = async () => {
    await auth0.logout({
      returnTo: window.location.origin,
    });
  };

  // Cross-tab: respond ONLY to NEW_LOGIN and LOGOUT events
  useEffect(() => {
    const tabId = myTabId();

    return onAuthBusMessage(msg => {
      if (msg.tabId === tabId) return;

      if (msg.type === 'NEW_LOGIN') {
        // Only stale if this tab is not already aligned to the active identity
        const activeSub = localStorage.getItem(ACTIVE_SUB_KEY) || '';
        if (activeSub && msg.sub === activeSub) return;

        sessionStorage.setItem(STALE_KEY, '1');
        sessionStorage.removeItem(LOGOUT_KEY);
        setIsLoggedOut(false);
        setIsStale(true);
      }

      if (msg.type === 'LOGOUT') {
        // Clear only app state in THIS tab (do not call auth0.logout here)
        localStorage.removeItem('accessToken');
        localStorage.removeItem(ACTIVE_SUB_KEY);
        sessionStorage.removeItem(LAST_BROADCAST_KEY);

        sessionStorage.setItem(LOGOUT_KEY, '1');
        sessionStorage.removeItem(STALE_KEY);
        setIsStale(false);
        setIsLoggedOut(true);
      }
    });
  }, []);

  // check for token expiration every 5 seconds
  useEffect(() => {
    const intervalId = setInterval(async () => {
      if (accessToken) {
        // existing session detected, confirm if not expired
        verifyAuthExpiration(jwtPayload(accessToken), auth0);
      }
    }, 5000);

    return () => clearInterval(intervalId);
  }, [accessToken, auth0]);

  const accessTokenRef = useRef(accessToken);
  accessTokenRef.current = accessToken;

  // Re-authenticate after every Socket.IO connect (including automatic reconnect).
  // Also emit when the access token changes while already connected (e.g. first token after login).
  useEffect(() => {
    const emitRtLogin = () => {
      if (isStale || isLoggedOut) return;
      const token = accessTokenRef.current;
      if (!token) return;
      socket.emit('login', token);
    };

    const onConnect = () => emitRtLogin();

    socket.on('connect', onConnect);
    if (socket.connected) emitRtLogin();

    return () => {
      socket.off('connect', onConnect);
    };
  }, [accessToken, isStale, isLoggedOut]);

  const RequiresLogin = () => {
    useEffect(() => {
      if (isStale || isLoggedOut) return;
      if (authErrorMessage) return;

      let cancelled = false;

      const checkAuthAndInitialize = async () => {
        if (auth0.isLoading) return;

        if (!auth0.user) {
          await auth0.loginWithRedirect({ appState: { returnTo: window.location.hash } });
          return;
        }

        const sub = auth0.user.sub || '';
        const prevSub = localStorage.getItem(ACTIVE_SUB_KEY) || '';

        // Read current stored token ONCE
        const currentRaw = localStorage.getItem('accessToken');
        const current = currentRaw ? JSON.parse(currentRaw) : '';

        // Get token (default cache to avoid churn)
        let token = '';
        try {
          token = await auth0.getAccessTokenSilently();
        } catch (e) {
          console.warn(e);
          await auth0.loginWithRedirect({ appState: { returnTo: window.location.hash } });
          return;
        }

        if (cancelled) return;

        const identityChanged = sub && sub !== prevSub;

        // Persist token only when needed (prevents cross-tab render loops)
        const shouldPersist = !current || isTokenNearExpiry(current, 60) || identityChanged;

        if (shouldPersist) {
          updateAccessToken(token);
        }

        // Broadcast only on identity change
        if (identityChanged) {
          localStorage.setItem(ACTIVE_SUB_KEY, sub);
          sessionStorage.setItem(LAST_BROADCAST_KEY, token);
          announceNewLogin(sub);
        }
      };

      checkAuthAndInitialize();

      return () => {
        cancelled = true;
      };

    }, [isStale, isLoggedOut, authErrorMessage, auth0.isLoading, !!auth0.user?.sub]);
    if (signInDenied) {
      return <SignInDenied onReturnToLogin={handleReturnToLogin} />;
    }

    if (authErrorMessage) {
      return (
        <QContainer style={{ padding: '24px', maxWidth: '720px', margin: '48px auto', textAlign: 'center' }}>
          <QContainer sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <QTypography variant="h2Medium">Unable to sign in</QTypography>
            <QTypography variant="bodyRegular">{authErrorMessage}</QTypography>
          </QContainer>
          <QButton
            variant="contained"
            onClick={handleReturnToLogin}
            sx={{
              marginTop: '16px',
              padding: '10px 16px',
              borderRadius: '6px',
              border: '1px solid #1976d2',
              background: '#1976d2',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            Return to login page
          </QButton>
        </QContainer>
      );
    }

    return <Outlet />;
  };

  const Logout = () => {
    useEffect(() => {
      // Tell other tabs to clear app state + show logged-out overlay
      setIsStale(false);
      setIsLoggedOut(false);
      logoutLocal(auth0);
    }, []);

    return null;
  };

  // ---- Overlay actions
  const handleStaleReload = () => {
    sessionStorage.removeItem(STALE_KEY);
    setIsStale(false);
    window.location.reload();
  };

  const handleLoggedOutReload = () => {
    sessionStorage.removeItem(LOGOUT_KEY);
    setIsLoggedOut(false);
    window.location.reload();
  };

  // Render app as usual
  if (ldClient.waitUntilReady)
    return (
      <RollbarProvider config={rollbarConfig}>
        <CacheProvider value={cache}>
          <QThemeProvider>
            <SnackbarProvider anchorOrigin={{ horizontal: 'right', vertical: 'top' }}>
              <CssBaseline />
              <BackgroundProgressBarProvider>
                <NavigationProvider>
                  <Router>
                    <OrgProvider accessToken={accessToken}>
                      <Routes>
                        <Route element={<RequiresLogin />}>
                          <Route path="/" element={<Index />} />
                          <Route path="/no-access" element={<NoAccess />} />
                          <Route element={<Layout />}>
                            <Route element={<ProtectedRoute authPermission={AuthPermissions.MENU_DASHBOARD} />}>
                              <Route path="/dashboard" element={<Dashboard />} />
                            </Route>
                            <Route path="/devices" element={<Devices />} />
                            <Route path="/device-details" element={<DeviceDetails />} />
                            <Route element={<ProtectedRoute authPermission={AuthPermissions.MENU_APPLICATIONS} />}>
                              <Route path="/applications" element={<Applications />} />
                            </Route>
                            <Route element={<ProtectedRoute authPermission={AuthPermissions.READ_APP} />}>
                              <Route path="/applications/app-detail-report" element={<SarifReport />} />
                              <Route
                                path="/new-report-pdf-view/:analysisId"
                                element={
                                  <Suspense fallback={<div>Generating PDF Report...</div>}>
                                    <NewReportPdfView />
                                  </Suspense>
                                }
                              />
                            </Route>
                            <Route path="/device-owners" element={<DeviceOwners />} />
                            <Route path="/settings" element={<Settings />} />
                            <Route path="/organization-mismatch-error" element={<OrgMismatchError />} />
                            <Route path="/customer-support" element={<CustomerSupport />} />
                          </Route>
                          {/* managed apps, policies, risk indicators, request analysis layout rely on respective context variables - rendered separately */}
                          <Route path="/app-management/*" element={<AppManagement />} />
                          <Route path="/policies/*" element={<Policies />} />
                          <Route
                            element={
                              <ProtectedRoute authPermission={[AuthPermissions.MENU_RISK_INDICATORS, AuthPermissions.SYSTEM_ADMIN]} />
                            }
                          >
                            <Route path="/risk-indicators/*" element={<RiskIndicators />} />
                          </Route>
                          <Route element={<ProtectedRoute authPermission={AuthPermissions.MENU_REQUEST_ANALYSIS} />}>
                            <Route path="/request-analysis/*" element={<RequestAnalysis />} />
                          </Route>
                          <Route path="/logout" element={<Logout />} />
                        </Route>
                        <Route path="/okta-troubleshooting" element={<OktaTroubleshooting />} />
                      </Routes>

                      {isStale && <StaleOverlay onReload={handleStaleReload} />}
                      {isLoggedOut && <LoggedOutOverlay onReload={handleLoggedOutReload} />}
                    </OrgProvider>
                  </Router>
                </NavigationProvider>
              </BackgroundProgressBarProvider>
            </SnackbarProvider>
          </QThemeProvider>
        </CacheProvider>
      </RollbarProvider>
    );

  return null;
}
